import axios, { AxiosResponse } from 'axios';
import { ColorFormData, ColorListParams, PaginatedColorResponse } from '../types/color';

// Prefer build-time env var (CRA requires REACT_APP_ prefix); fallback to localhost for dev
const API_BASE_URL = (process.env.REACT_APP_API_BASE || 'http://localhost:8000/api/v1').replace(/\/$/, '');

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Batch preview & save types
export type BatchHorseItem = { id: number; name: string; tab_no: number; colours: string };
export type BatchPreviewCard = {
  id: number; name: string; tab_no: number; colours: string; approved: boolean;
  front_svg: string; side_svg: string;
};
export type SavePreviewRequest = { id: number; name: string; tab_no: number; colours: string; front_svg: string; side_svg: string; preview_string?: string; original_string?: string };
export type SavePreviewResponse = { saved: boolean; approved: boolean; front_path?: string; side_path?: string };

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface Jersey {
  id: number;
  name: string;
  description: string;
  status: string;
  parsed: boolean;
  front_view_url?: string;
  side_view_url?: string;
  confidence_score?: number;
  errors: string[];
  race_id: number;
  trainer_id?: number;
  number?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ParseRequest {
  description: string;
}

export interface ParseResponse {
  base_color: string | null;
  elements: JerseyElement[];
  confidence_score: number;
  errors: string[];
}

export interface JerseyElement {
  element_type: string;
  color: string | null;
  patterns: string[];
  pattern_colors?: Record<string, string[]>;
  order: number;
  // Additional parser metadata
  explicit_color?: boolean;
  visibility_adjusted?: boolean;
  raw_text?: string;
  colors?: string[];
}

export interface Race {
  id: number;
  name: string;
  number: number;
  race_time: string;
  approved: boolean;
  meeting_id: number;
}

export interface Meeting {
  id: number;
  location: string;
  date: string;
  external_id: string;
}

export interface Colour {
  id: number;
  name: string;
  hexadecimal: string;
  cmyk?: string;
  rgb?: string;
}

export interface ElementType {
  id: number;
  name: string;
  description?: string;
}

export interface Pattern {
  id: number;
  description: string;
  slug: string;
  front_svg_content?: string;
  side_svg_content?: string;
  matching_string?: string;
  absolute_positioning?: boolean | null;
  absolute_positioning_front?: boolean | null;
  absolute_positioning_side?: boolean | null;
  is_logo: boolean;
  status: boolean;
  element_type_id: number;
  created_at?: string;
  updated_at?: string;
}

export interface PaginatedPatternResponse {
  items: Pattern[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PatternCreate {
  description: string;
  element_type_id: number;
  matching_string?: string;
  absolute_positioning?: boolean | null;
  absolute_positioning_front?: boolean | null;
  absolute_positioning_side?: boolean | null;
  is_logo?: boolean;
  status?: boolean;
}

export interface PatternUpdate {
  description?: string;
  element_type_id?: number;
  matching_string?: string;
  absolute_positioning?: boolean | null;
  absolute_positioning_front?: boolean | null;
  absolute_positioning_side?: boolean | null;
  is_logo?: boolean;
  status?: boolean;
}

export interface PatternListParams {
  page?: number;
  size?: number;
  element_type_id?: number;
  status?: boolean;
  search?: string;
  lightweight?: boolean;
  include_svg?: boolean;
}

export interface Trainer {
  id: number;
  name: string;
  external_id?: string;
}

export interface Synonym {
  id: number;
  term: string;
  original_term: string;
  colour_id?: number;
  pattern_id?: number;
  element_type_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface SynonymCreate {
  term: string;
  original_term: string;
  colour_id?: number;
  pattern_id?: number;
  element_type_id?: number;
}

// API functions
export const jerseyApi = {
  // Jersey endpoints
  async getJerseys(params?: {
    skip?: number;
    limit?: number;
    race_id?: number;
    status?: string;
    search?: string;
  }): Promise<Jersey[]> {
    const response: AxiosResponse<Jersey[]> = await apiClient.get('/jerseys/', {
      params,
    });
    return response.data;
  },

  async getJersey(id: number): Promise<Jersey> {
    const response: AxiosResponse<Jersey> = await apiClient.get(`/jerseys/${id}`);
    return response.data;
  },

  async createJersey(data: {
    name: string;
    description: string; // editable string
    race_id: number;
    trainer_id?: number;
    number?: number;
    original_colour_string?: string; // immutable original
  }): Promise<Jersey> {
    const response: AxiosResponse<Jersey> = await apiClient.post('/jerseys/', data);
    return response.data;
  },

  async updateJersey(id: number, data: Partial<Jersey>): Promise<Jersey> {
    const response: AxiosResponse<Jersey> = await apiClient.put(`/jerseys/${id}`, data);
    return response.data;
  },

  async deleteJersey(id: number): Promise<void> {
    await apiClient.delete(`/jerseys/${id}`);
  },

  async saveJersey(data: any): Promise<Jersey> {
    if (data.id) {
      return this.updateJersey(data.id, data);
    } else {
      return this.createJersey(data);
    }
  },

  // Parsing endpoints
  async parseDescription(data: ParseRequest): Promise<ParseResponse> {
    const response: AxiosResponse<ParseResponse> = await apiClient.post('/parse/', data);
    return response.data;
  },

  async parseJerseyDescription(description: string): Promise<ParseResponse> {
    const response: AxiosResponse<ParseResponse> = await apiClient.post('/parse/', { description });
    return response.data;
  },

  // Generation endpoints
  async generateSVG(data: { jersey_id: number; views: string[]; }): Promise<{ message: string; jersey_id: number }> {
    const response = await apiClient.post(`/jerseys/${data.jersey_id}/generate`, data);
    return response.data;
  },

  // Approved schemes CRUD
  // Approved schemes CRUD
  async listApprovedSchemes(params?: { skip?: number; limit?: number; search?: string; approved?: boolean; include_svg?: boolean; presign?: boolean }): Promise<{ items: any[]; total: number }> {
    const response = await apiClient.get('/approved-schemes', { params });
    return response.data;
  },
  async updateApprovedScheme(id: number, data: { original_string?: string; approved?: boolean }): Promise<any> {
    const response = await apiClient.put(`/approved-schemes/${id}`, data);
    return response.data;
  },
  async deleteApprovedScheme(id: number): Promise<void> {
    await apiClient.delete(`/approved-schemes/${id}`);
  },
  async lookupApprovedSchemeByColours(colours: string): Promise<{ id: number; original_string: string; normalised_string: string; approved: boolean; preview_string?: string | null }> {
    const response = await apiClient.get('/approved-schemes/lookup', { params: { colours } });
    return response.data;
  },

  async generateSVGPreview(data: {
    description?: string;
    view: 'front' | 'side';
    elements?: any[];
    base_color?: string | null;
    race_id?: number;
    trainer_id?: number;
  }): Promise<string> {
    const response = await apiClient.post('/preview/svg', data, { responseType: 'text' });
    return response.data;
  },

  async analyzePatternTransforms(data: { svg: string; view?: 'front' | 'side' }): Promise<{
    mismatches: Array<{
      element_id: string;
      pattern_slug: string;
      pattern_id?: number | null;
      pattern_group_id: string;
      library_transform: string;
      parent_transform?: string | null;
      jersey_transform: string;
      missing_transform?: string | null;
    }>;
  }> {
    const response = await apiClient.post('/preview/analyze-transforms', data);
    return response.data;
  },

  async applyPatternTransform(data: {
    svg: string;
    pattern_group_id: string;
    transform: string;
  }): Promise<{ svg: string; success: boolean }> {
    const response = await apiClient.post('/preview/apply-transform', data);
    return response.data;
  },

  // Race endpoints
  async getRaces(params?: {
    skip?: number;
    limit?: number;
    meeting_id?: number;
  }): Promise<Race[]> {
    const response: AxiosResponse<Race[]> = await apiClient.get('/races/', {
      params,
    });
    return response.data;
  },

  async getRace(id: number): Promise<Race> {
    const response: AxiosResponse<Race> = await apiClient.get(`/races/${id}`);
    return response.data;
  },

  // Trainer endpoints
  async getTrainers(params?: {
    skip?: number;
    limit?: number;
    search?: string;
  }): Promise<Trainer[]> {
    const response: AxiosResponse<Trainer[]> = await apiClient.get('/trainers/', {
      params,
    });
    return response.data;
  },

  // Meeting endpoints
  async getMeetings(params?: {
    skip?: number;
    limit?: number;
    date?: string;
  }): Promise<Meeting[]> {
    const response: AxiosResponse<Meeting[]> = await apiClient.get('/meetings/', {
      params,
    });
    return response.data;
  },

  async getMeeting(id: number): Promise<Meeting> {
    const response: AxiosResponse<Meeting> = await apiClient.get(`/meetings/${id}`);
    return response.data;
  },

  // Reference data endpoints
  async getColours(): Promise<Colour[]> {
    const response: AxiosResponse<Colour[]> = await apiClient.get('/colours/');
    return response.data;
  },

  // Color CRUD operations
  async createColor(data: ColorFormData): Promise<Colour> {
    const response: AxiosResponse<Colour> = await apiClient.post('/colours/', data);
    return response.data;
  },

  async getColor(id: number): Promise<Colour> {
    const response: AxiosResponse<Colour> = await apiClient.get(`/colours/${id}`);
    return response.data;
  },

  async updateColor(id: number, data: Partial<ColorFormData>): Promise<Colour> {
    const response: AxiosResponse<Colour> = await apiClient.put(`/colours/${id}`, data);
    return response.data;
  },

  async deleteColor(id: number): Promise<void> {
    await apiClient.delete(`/colours/${id}`);
  },

  // Batch preview endpoint
  async batchPreview(horses: BatchHorseItem[]): Promise<BatchPreviewCard[]> {
    const response: AxiosResponse<BatchPreviewCard[]> = await apiClient.post('/preview/batch', { horses });
    return response.data;
  },

  // Save preview endpoint
  async savePreview(req: SavePreviewRequest): Promise<SavePreviewResponse> {
    const response: AxiosResponse<SavePreviewResponse> = await apiClient.post('/preview/save', req);
    return response.data;
  },

  async listColors(params?: ColorListParams): Promise<PaginatedColorResponse> {
    const response: AxiosResponse<PaginatedColorResponse> = await apiClient.get('/colours/', {
      params,
    });
    return response.data;
  },

  async saveColor(data: ColorFormData & { id?: number }): Promise<Colour> {
    if (data.id) {
      const { id, ...updateData } = data;
      return this.updateColor(id, updateData);
    } else {
      return this.createColor(data);
    }
  },

  async getElementTypes(): Promise<ElementType[]> {
    const response: AxiosResponse<ElementType[]> = await apiClient.get('/element-types/');
    return response.data;
  },

  async getPatterns(params?: PatternListParams): Promise<PaginatedPatternResponse> {
    const response: AxiosResponse<PaginatedPatternResponse> = await apiClient.get('/patterns/', {
      params,
    });
    return response.data;
  },

  async getPattern(id: number): Promise<Pattern> {
    const response: AxiosResponse<Pattern> = await apiClient.get(`/patterns/${id}`);
    return response.data;
  },

  async createPattern(data: PatternCreate): Promise<Pattern> {
    const response: AxiosResponse<Pattern> = await apiClient.post('/patterns/', data);
    return response.data;
  },

  async updatePattern(id: number, data: PatternUpdate): Promise<Pattern> {
    const response: AxiosResponse<Pattern> = await apiClient.put(`/patterns/${id}`, data);
    return response.data;
  },

  async deletePattern(id: number): Promise<void> {
    await apiClient.delete(`/patterns/${id}`);
  },

  async uploadPatternSVG(id: number, view: 'front' | 'side', file: File): Promise<{ message: string; content_length: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('view', view);

    const response: AxiosResponse<{ message: string; content_length: number }> = await apiClient.post(
      `/patterns/${id}/upload-svg`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  async getDashboardStats(): Promise<any> {
    const response = await apiClient.get('/stats/dashboard');
    return response.data;
  },

  // New Silks APIs
  async parseSilks(data: { id: number; name: string; tab_no: number; colours: string }): Promise<{ uuid: string; temp_front: string; temp_side: string; approved: boolean }> {
    const response = await apiClient.post('/silks/parse', data);
    return response.data;
  },

  async approveSilks(data: { id: number; uuid: string; name: string; tab_no: number; colours: string; temp_front: string; temp_side: string }): Promise<any> {
    const response = await apiClient.post('/silks/approve', data);
    return response.data;
  },

  // Authentication: local dev uses backend login; staging/prod uses Cognito tokens
  async login(username: string, password: string): Promise<{
    access_token: string; token_type: string; expires_in: number;
  }> {
    const response = await apiClient.post('/auth/login', { username, password });
    return response.data;
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
    localStorage.removeItem('access_token');
  },

  async getCurrentUser(): Promise<any> {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  // Synonym endpoints
  async getSynonyms(params?: {
    skip?: number;
    limit?: number;
    colour_id?: number;
    pattern_id?: number;
    element_type_id?: number;
  }): Promise<Synonym[]> {
    const response: AxiosResponse<Synonym[]> = await apiClient.get('/synonyms/', {
      params,
    });
    return response.data;
  },

  async createSynonym(data: SynonymCreate): Promise<Synonym> {
    const response: AxiosResponse<Synonym> = await apiClient.post('/synonyms/', data);
    return response.data;
  },

  async updateSynonym(id: number, data: Partial<SynonymCreate>): Promise<Synonym> {
    const response: AxiosResponse<Synonym> = await apiClient.put(`/synonyms/${id}`, data);
    return response.data;
  },

  async deleteSynonym(id: number): Promise<void> {
    await apiClient.delete(`/synonyms/${id}`);
  },
};

export default jerseyApi;
